import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Building2, Plus, Loader2, ShieldCheck, Crown, AlertTriangle, ArrowLeft, Users, Briefcase, FileText, GraduationCap, Trash2, CreditCard, LogIn, CheckCircle2, TrendingUp, TrendingDown, Euro, Activity, Zap, Calendar, Database, ClipboardList, Download, Upload, Clock, Filter, Sparkles, MessageSquare } from 'lucide-react';
import api, { setToken } from '../api/client';
import Sante from './superadmin/Sante';
import { toast } from '../hooks/use-toast';
import { useAuth } from '../contexts/AuthContext';
import DemosTab from '../components/superadmin/DemosTab';
import ForceStatusDialog from '../components/superadmin/ForceStatusDialog';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../components/ui/dialog';

/**
 * SuperAdmin back-office (Vague 97.5 Phase 1).
 * Cross-tenant management for the platform owner.
 *
 * Phase 1 = tenant CRUD + plan catalog + per-tenant usage metrics.
 * Phase 2 (future) = Stripe billing, auto quota enforcement, impersonation.
 * Phase 3 (future) = DB-per-tenant or schema-per-tenant physical isolation.
 */
export default function SuperAdmin() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [tab, setTab] = useState('tenants');
  const [tenants, setTenants] = useState([]);
  const [plans, setPlans] = useState([]);
  const [moduleCatalog, setModuleCatalog] = useState([]);
  const [billingMode, setBillingMode] = useState(null); // { mode, sandbox, key_prefix }
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [metricsOpen, setMetricsOpen] = useState(null); // tenant or null
  const [paymentToast, setPaymentToast] = useState(null); // { status, attempts }
  const [forceStatusOpen, setForceStatusOpen] = useState(false);

  // Hard guard — non-superadmins see a friendly redirect.
  const role = (user?.role || '').toLowerCase().replace(' ', '_');
  const isSuperAdmin = role === 'super_admin' || role === 'superadmin';

  const reload = async () => {
    setLoading(true);
    setError(null);
    try {
      const [tRes, pRes, mRes, cRes] = await Promise.all([
        api.get('/superadmin/tenants'),
        api.get('/superadmin/plans'),
        api.get('/superadmin/billing/mode').catch(() => ({ data: null })),
        api.get('/superadmin/modules/catalog').catch(() => ({ data: { catalog: [] } })),
      ]);
      setTenants(tRes.data);
      setPlans(pRes.data);
      setBillingMode(mRes.data);
      setModuleCatalog(cRes.data?.catalog || []);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Impossible de charger les données super admin.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isSuperAdmin) reload();
    else setLoading(false);
  }, [isSuperAdmin]);

  // Vague 97.6 — Stripe return polling
  useEffect(() => {
    const sid = searchParams.get('session_id');
    if (!sid || !isSuperAdmin) return;
    let cancelled = false;
    let attempts = 0;
    const max = 6;
    const poll = async () => {
      if (cancelled || attempts >= max) {
        if (attempts >= max) setPaymentToast({ status: 'timeout' });
        return;
      }
      attempts += 1;
      setPaymentToast({ status: 'polling', attempts });
      try {
        const { data } = await api.get(`/billing/status/${encodeURIComponent(sid)}`);
        if (data.payment_status === 'paid') {
          setPaymentToast({ status: 'paid', plan_code: data.plan_code, org_id: data.org_id });
          toast({ title: 'Paiement validé ✅', description: `Abonnement activé pour ${data.org_id}` });
          await reload();
          setSearchParams({}, { replace: true });
          return;
        }
        if (data.status === 'expired') {
          setPaymentToast({ status: 'expired' });
          setSearchParams({}, { replace: true });
          return;
        }
      } catch (e) {
        console.warn("[silent]", e);
      }
      setTimeout(poll, 2_000);
    };
    poll();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams.get('session_id'), isSuperAdmin]);

  if (!isSuperAdmin) {
    return (
      <div className="min-h-screen bg-[#FAFAFE] flex items-center justify-center px-6">
        <div className="max-w-[440px] text-center" data-testid="superadmin-denied">
          <div className="w-16 h-16 rounded-2xl bg-[#FEF3C7] text-[#92400E] mx-auto mb-4 flex items-center justify-center">
            <AlertTriangle className="w-7 h-7" />
          </div>
          <h1 className="text-[24px] font-bold text-[#1A1B2E] mb-2">Accès Super Admin requis</h1>
          <p className="text-[14px] text-[#6B6C85] mb-5">
            Cette zone est réservée aux administrateurs de la plateforme. Si vous pensez qu'il s'agit d'une erreur, contactez votre équipe technique.
          </p>
          <button
            data-testid="superadmin-back"
            onClick={() => nav('/app')}
            className="h-10 px-5 rounded-xl brand-gradient text-white text-[13px] font-semibold inline-flex items-center gap-1.5"
          >
            <ArrowLeft className="w-4 h-4" /> Retour à l'application
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFAFE]" data-testid="superadmin-page">
      {/* Header */}
      <header className="border-b border-[#EFF0F6] bg-white px-8 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#4F46E5] to-[#5B3FE0] flex items-center justify-center">
            <Crown className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="text-[20px] font-bold text-[#1A1B2E]" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Super Admin</h1>
            <p className="text-[12px] text-[#6B6C85]">Gestion multi-tenants &amp; abonnements</p>
          </div>
          {billingMode && (
            <span
              data-testid="billing-mode-badge"
              title={billingMode.sandbox
                ? `Sandbox Stripe (${billingMode.key_prefix || 'sk_test_emergent'}) — paiement one-shot uniquement. Cf. /app/memory/STRIPE_SUBSCRIPTION_MODE.md.`
                : `Vraie clé Stripe ${billingMode.key_prefix} — souscriptions auto-renouvelables actives.`}
              className={`ml-3 inline-flex items-center gap-1 px-2.5 h-6 rounded-full text-[11px] font-semibold border ${
                billingMode.mode === 'subscription'
                  ? 'bg-[#E6FBF7] text-[#0F766E] border-[#99F6E4]'
                  : 'bg-[#FEF3C7] text-[#92400E] border-[#FCD34D]'
              }`}
            >
              {billingMode.mode === 'subscription' ? '🔁 Auto-renew actif' : '⚠ Sandbox · One-shot'}
            </span>
          )}
        </div>
        <button onClick={() => logout()} data-testid="superadmin-logout" className="h-9 px-3 rounded-lg border border-[#EFF0F6] text-[12.5px] text-[#4A4B63] font-semibold flex items-center gap-1.5 hover:bg-[#F6F7FB]">
          <ArrowLeft className="w-3.5 h-3.5" /> Déconnexion
        </button>
      </header>

      {/* Tabs */}
      <div className="px-8 pt-6 flex items-center justify-between gap-3">
        <div className="flex bg-white border border-[#EFF0F6] rounded-xl p-1 w-fit shadow-sm">
          {[
            { id: 'metrics', label: 'Metrics', icon: TrendingUp },
            { id: 'tenants', label: 'Tenants', icon: Building2 },
            { id: 'demos', label: 'Demos', icon: Sparkles },
            { id: 'plans', label: 'Forfaits', icon: ShieldCheck },
            { id: 'ops', label: 'Opérations', icon: Database },
            { id: 'audit', label: 'Audit', icon: ClipboardList },
          ].map((t) => (
            <button
              key={t.id}
              data-testid={`superadmin-tab-${t.id}`}
              onClick={() => setTab(t.id)}
              className={`h-9 px-4 rounded-lg text-[12.5px] font-semibold flex items-center gap-1.5 ${tab === t.id ? 'bg-[#EEF2FF] text-[#4F46E5]' : 'text-[#6B6C85] hover:bg-[#FAFAFE]'}`}
            >
              <t.icon className="w-3.5 h-3.5" /> {t.label}
            </button>
          ))}
        </div>
        <button
          onClick={() => setForceStatusOpen(true)}
          data-testid="superadmin-open-force-status"
          className="h-9 px-3 rounded-lg bg-white border border-[#EFF0F6] text-[#4F46E5] text-[12px] font-semibold flex items-center gap-1.5 hover:bg-[#EEF2FF] shadow-sm"
          title="Récupérer / forcer le statut d'un tenant ou démo"
        >
          🔧 Récupérer un tenant
        </button>
      </div>

      <main className="px-8 py-6">
        {paymentToast && (
          <div className={`mb-4 rounded-xl p-4 flex items-center gap-3 ${paymentToast.status === 'paid' ? 'bg-[#E6FBF7] border border-[#99F6E4] text-[#0F766E]' : paymentToast.status === 'expired' || paymentToast.status === 'timeout' ? 'bg-[#FEF2F2] border border-[#FECACA] text-[#991B1B]' : 'bg-[#FEF3C7] border border-[#FCD34D] text-[#92400E]'}`} data-testid="payment-banner">
            {paymentToast.status === 'paid' ? <CheckCircle2 className="w-5 h-5 shrink-0" /> : paymentToast.status === 'expired' || paymentToast.status === 'timeout' ? <AlertTriangle className="w-5 h-5 shrink-0" /> : <Loader2 className="w-5 h-5 animate-spin shrink-0" />}
            <div className="flex-1 text-[13px]">
              {paymentToast.status === 'paid' && <span className="font-semibold">✅ Paiement validé — abonnement activé. La table ci-dessous a été rafraîchie.</span>}
              {paymentToast.status === 'polling' && <span>Vérification du paiement Stripe… (essai {paymentToast.attempts}/15)</span>}
              {paymentToast.status === 'expired' && <span>Le paiement a expiré ou été annulé. Réessayez quand vous voulez.</span>}
              {paymentToast.status === 'timeout' && <span>Statut introuvable après plusieurs tentatives. Vérifiez l'email de confirmation Stripe ou réessayez.</span>}
            </div>
            <button onClick={() => setPaymentToast(null)} className="text-[11px] opacity-70 hover:opacity-100">✕</button>
          </div>
        )}

        {loading ? (
          <div className="text-[14px] text-[#6B6C85] flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Chargement…</div>
        ) : error ? (
          <div className="rounded-xl bg-[#FEF2F2] border border-[#FECACA] text-[#991B1B] px-4 py-3 text-[13px]" data-testid="superadmin-error">{error}</div>
        ) : tab === 'metrics' ? (
          <MetricsTab />
        ) : tab === 'tenants' ? (
          <TenantsTab tenants={tenants} plans={plans} billingMode={billingMode} onCreate={() => setCreateOpen(true)} onMetrics={(t) => setMetricsOpen(t)} reload={reload} />
        ) : tab === 'demos' ? (
          <DemosTab />
        ) : tab === 'plans' ? (
          <PlansTab plans={plans} moduleCatalog={moduleCatalog} reload={reload} />
        ) : tab === 'ops' ? (
          <OpsTab />
        ) : (
          <AuditTab tenants={tenants} />
        )}
      </main>

      <CreateTenantDialog open={createOpen} onOpenChange={setCreateOpen} plans={plans} onCreated={reload} />
      <TenantMetricsDialog tenant={metricsOpen} onOpenChange={() => setMetricsOpen(null)} />
      <ForceStatusDialog open={forceStatusOpen} onOpenChange={setForceStatusOpen} onSuccess={reload} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tenants tab
// ---------------------------------------------------------------------------
// « il y a 3 jours », « il y a 2 h » : la date brute oblige à calculer, et
// c'est l'écart qui compte — un espace muet depuis deux mois se voit.
function depuis(iso) {
  const ms = Date.now() - new Date(iso).getTime();
  if (!Number.isFinite(ms) || ms < 0) return '';
  const min = Math.floor(ms / 60000);
  if (min < 60) return `il y a ${Math.max(1, min)} min`;
  const h = Math.floor(min / 60);
  if (h < 24) return `il y a ${h} h`;
  const j = Math.floor(h / 24);
  if (j < 60) return `il y a ${j} jour${j > 1 ? 's' : ''}`;
  return `le ${new Date(iso).toLocaleDateString('fr-FR')}`;
}

function TenantsTab({ tenants, plans, billingMode, onCreate, onMetrics, reload }) {
  const [reconciliation, setReconciliation] = useState(false);
  const [rapport, setRapport] = useState(null);
  const reconcilier = async () => {
    setReconciliation(true);
    try {
      const { data } = await api.post('/superadmin/registre/reconcilier');
      setRapport(data);
      await reload();
    } catch (err) {
      toast({ title: 'Vérification impossible', description: err?.response?.data?.detail || '', variant: 'destructive' });
    } finally {
      setReconciliation(false);
    }
  };
  const { setActingOrg } = useAuth();
  const navigate = useNavigate();

  // Vague 97.13 — "Accéder à cet OF" = adopt the tenant's context for the
  // current super_admin session (no logout, no token swap). All API calls
  // will carry the X-Acting-Org-Id header until the user clicks "Quitter".
  const enterTenant = (t) => {
    setActingOrg({ org_id: t.org_id, name: t.name });
    toast({ title: `Mode super admin actif`, description: `Vous gérez désormais l'OF « ${t.name} ».` });
    navigate('/app');
  };

  const updateTenant = async (org_id, patch) => {
    try {
      await api.patch(`/superadmin/tenants/${org_id}`, patch);
      toast({ title: 'Tenant mis à jour' });
      await reload();
    } catch (e) {
      toast({ title: 'Erreur', description: e?.response?.data?.detail || 'Mise à jour impossible' });
    }
  };

  // Wave 1u9 — dedicated suspend / reactivate endpoints (revoke sessions + audit log)
  const changeStatus = async (t, nextStatus) => {
    if (nextStatus === t.status) return;
    if (nextStatus === 'suspended') {
      const reason = window.prompt(
        `Suspendre « ${t.name} » ?\n\nCela révoque toutes les sessions actives et empêche les utilisateurs de se reconnecter. Motif (optionnel) :`,
        '',
      );
      if (reason === null) return; // user cancelled
      try {
        const { data } = await api.post(`/superadmin/tenants/${t.org_id}/suspend`, { reason: reason || undefined });
        toast({ title: 'Tenant suspendu', description: `${data.sessions_revoked} session(s) révoquée(s).` });
        await reload();
      } catch (e) {
        toast({ title: 'Erreur', description: e?.response?.data?.detail || 'Suspension impossible' });
      }
      return;
    }
    if (t.status === 'suspended') {
      try {
        await api.post(`/superadmin/tenants/${t.org_id}/reactivate`);
        toast({ title: 'Tenant réactivé' });
        await reload();
      } catch (e) {
        toast({ title: 'Erreur', description: e?.response?.data?.detail || 'Réactivation impossible' });
      }
      return;
    }
    // trial ↔ active swap — keep plain PATCH
    await updateTenant(t.org_id, { status: nextStatus });
  };

  const deleteTenant = async (t) => {
    if (!window.confirm(`Supprimer ${t.name} ? Cette action est irréversible. Les données conservent leur org_id mais le tenant n'apparaîtra plus dans la console.`)) return;
    try {
      await api.delete(`/superadmin/tenants/${t.org_id}`);
      toast({ title: 'Tenant supprimé' });
      await reload();
    } catch (e) {
      toast({ title: 'Erreur', description: e?.response?.data?.detail || 'Suppression impossible' });
    }
  };

  // Vague 97.6 Phase 2 — open Stripe Checkout for a tenant
  const startCheckout = async (t, cycle = 'month') => {
    try {
      const { data } = await api.post(`/superadmin/tenants/${t.org_id}/checkout`, {
        origin_url: window.location.origin,
        cycle,
      });
      // Hard redirect — Stripe-hosted page
      window.location.href = data.url;
    } catch (e) {
      toast({ title: 'Erreur', description: e?.response?.data?.detail || 'Checkout indisponible' });
    }
  };

  // Vague 97.9 — TRUE recurring subscription (real Stripe key only)
  const startSubscription = async (t, cycle = 'month') => {
    try {
      const { data } = await api.post(`/superadmin/tenants/${t.org_id}/subscribe`, {
        origin_url: window.location.origin,
        cycle,
      });
      window.location.href = data.url;
    } catch (e) {
      toast({
        title: 'Mode subscription indisponible',
        description: e?.response?.data?.detail || "Une vraie clé Stripe est requise. Cf. /app/memory/STRIPE_SUBSCRIPTION_MODE.md.",
      });
    }
  };

  // Vague 97.6 Phase 4 — impersonate a tenant admin
  const impersonate = async (t) => {
    if (!window.confirm(
      `Vous allez vous connecter en tant qu'administrateur de "${t.name}" pour 30 minutes.\n\n`
      + 'Cette action est tracée dans l\'audit log. Continuer ?',
    )) return;
    try {
      const { data } = await api.post(`/superadmin/tenants/${t.org_id}/impersonate`, {});
      // Replace our session token; navigate to /app to land in the impersonated tenant.
      setToken(data.token);
      // Stash impersonator markers so the FE can show a banner + restore the original session.
      try {
        sessionStorage.setItem('ifaap_impersonation', JSON.stringify({
          target_email: data.target_email,
          target_name: data.target_name,
          tenant_name: data.tenant_name,
          org_id: data.org_id,
          expires_at: data.expires_at,
        }));
      } catch (e) { console.warn("[silent]", e); }
      window.location.href = '/app';
    } catch (e) {
      toast({ title: 'Impossible', description: e?.response?.data?.detail || 'Impersonation refusée' });
    }
  };

  return (
    <>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-[18px] font-bold text-[#1A1B2E]" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Espaces ({tenants.length})</h2>
          <p className="text-[12.5px] text-[#6B6C85]">Un espace par organisme client, isolé par son <code className="bg-[#EEF2FF] px-1 rounded text-[#4F46E5]">org_id</code>. La liste est lue depuis le registre des organisations.</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            data-testid="tenants-reconcile-btn"
            onClick={reconcilier}
            disabled={reconciliation}
            title="Réaligne les fiches commerciales sur les organisations : une fiche par espace, jamais de fiche sans espace"
            className="h-10 px-3 rounded-xl bg-white border border-[#EFF0F6] text-[12.5px] font-semibold text-[#4A4B63] hover:bg-[#EEF2FF] disabled:opacity-50"
          >
            {reconciliation ? 'Vérification…' : 'Vérifier le registre'}
          </button>
          <button
            data-testid="tenants-create-btn"
            onClick={onCreate}
            className="h-10 px-4 rounded-xl brand-gradient text-white text-[13px] font-semibold flex items-center gap-1.5 shadow-sm"
          >
            <Plus className="w-4 h-4" /> Nouvel espace
          </button>
        </div>
      </div>
      {rapport && (
        <div data-testid="tenants-reconcile-report" className="mb-4 px-4 py-3 rounded-xl bg-[#F6F7FB] border border-[#EFF0F6] text-[12.5px] text-[#4A4B63]">
          {['rattachees', 'creees', 'renommees', 'orphelines'].every((k) => !(rapport[k] || []).length)
            ? 'Registre cohérent : chaque espace a sa fiche, chaque fiche a son espace.'
            : (
              <ul className="space-y-0.5">
                {(rapport.rattachees || []).map((r, i) => <li key={`r${i}`}>Fiche « {r.fiche} » rattachée à <code>{r.vers}</code> (elle pointait sur <code>{r.de}</code>, qui n'existe pas).</li>)}
                {(rapport.creees || []).map((r, i) => <li key={`c${i}`}>Fiche créée pour « {r.nom} » (<code>{r.org_id}</code>), qui n'en avait pas.</li>)}
                {(rapport.renommees || []).map((r, i) => <li key={`n${i}`}>« {r.de} » renommée « {r.vers} », le nom de l'organisation.</li>)}
                {(rapport.orphelines || []).map((r, i) => <li key={`o${i}`} className="text-[#B91C1C]">Fiche « {r.fiche} » (<code>{r.org_id}</code>) sans organisation et sans correspondance : à trancher à la main.</li>)}
              </ul>
            )}
        </div>
      )}

      <div className="bg-white border border-[#EFF0F6] rounded-2xl overflow-hidden">
        <table className="w-full text-[13px]">
          <thead className="bg-[#FAFAFE] text-[11.5px] font-semibold text-[#6B6C85] uppercase tracking-wide">
            <tr>
              <th className="text-left px-4 py-3">Espace</th>
              <th className="text-left px-4 py-3">Forfait</th>
              <th className="text-left px-4 py-3">Statut</th>
              <th className="text-left px-4 py-3">Contact</th>
              <th className="text-left px-4 py-3">Activité</th>
              <th className="text-right px-4 py-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {tenants.length === 0 && (
              <tr><td colSpan={6} className="text-center text-[#6B6C85] py-8 italic">Aucun espace. Cliquez sur « Nouvel espace » pour commencer.</td></tr>
            )}
            {tenants.map((t) => (
              <tr key={t.org_id} data-testid={`tenant-row-${t.org_id}`} className="border-t border-[#F3F4F8]">
                <td className="px-4 py-3">
                  <div className="font-semibold text-[#1A1B2E] flex items-center gap-2">
                    {t.name}
                    {t.organisation_existe === false && (
                      <span data-testid={`tenant-orphan-${t.org_id}`} title="Cette fiche pointe sur un espace qui n'existe pas : Accéder, Impersonate et Suspendre ne visent rien" className="text-[10px] px-1.5 py-0.5 rounded-full bg-[#FEF2F2] text-[#B91C1C] border border-[#FECACA] font-semibold">sans espace</span>
                    )}
                  </div>
                  <div className="text-[11px] text-[#8E8FA5] font-mono">{t.org_id}</div>
                </td>
                <td className="px-4 py-3">
                  <select
                    data-testid={`tenant-plan-${t.org_id}`}
                    value={t.plan_code || 'starter'}
                    onChange={(e) => updateTenant(t.org_id, { plan_code: e.target.value })}
                    className="h-8 px-2 rounded-lg bg-[#F6F7FB] border border-[#EFF0F6] text-[12.5px] font-semibold"
                  >
                    {plans.map((p) => <option key={p.code} value={p.code}>{p.name}</option>)}
                  </select>
                </td>
                <td className="px-4 py-3">
                  <select
                    data-testid={`tenant-status-${t.org_id}`}
                    value={t.status || 'trial'}
                    onChange={(e) => changeStatus(t, e.target.value)}
                    className={`h-8 px-2 rounded-lg text-[12px] font-semibold border ${t.status === 'active' ? 'bg-[#E6FBF7] text-[#0F766E] border-[#99F6E4]' : t.status === 'suspended' ? 'bg-[#FEF2F2] text-[#B91C1C] border-[#FECACA]' : t.status === 'pending' ? 'bg-[#EEF2FF] text-[#4F46E5] border-[#C7D2FE]' : 'bg-[#FEF3C7] text-[#92400E] border-[#FCD34D]'}`}
                  >
                    {t.status === 'pending' && <option value="pending" disabled>Onboarding en attente</option>}
                    <option value="trial">Essai</option>
                    <option value="active">Actif</option>
                    <option value="suspended">Suspendu</option>
                  </select>
                </td>
                <td className="px-4 py-3 text-[12.5px] text-[#4A4B63]">{t.contact_email}</td>
                <td className="px-4 py-3 text-[12px] text-[#4A4B63] whitespace-nowrap" data-testid={`tenant-activity-${t.org_id}`}>
                  <div>{t.utilisateurs ?? 0} utilisateur{(t.utilisateurs ?? 0) > 1 ? 's' : ''} · {t.formations ?? 0} formation{(t.formations ?? 0) > 1 ? 's' : ''}</div>
                  <div className="text-[11px] text-[#8E8FA5]" title={t.derniere_connexion_par || ''}>
                    {t.derniere_connexion ? `Dernière connexion ${depuis(t.derniere_connexion)}` : "Personne ne s'est encore connecté"}
                  </div>
                </td>
                <td className="px-4 py-3 text-right">
                  <button
                    onClick={() => startCheckout(t)}
                    data-testid={`tenant-checkout-${t.org_id}`}
                    title={`Activer l'abonnement (${(t.plan && t.plan.price_eur_month) || 0}€/mois) — paiement unique extension manuelle`}
                    className="h-8 px-3 rounded-lg bg-[#10B981] text-white text-[12px] font-semibold hover:bg-[#059669] mr-2 inline-flex items-center gap-1"
                  >
                    <CreditCard className="w-3.5 h-3.5" /> Payer
                  </button>
                  {billingMode?.mode === 'subscription' && (
                    <button
                      onClick={() => startSubscription(t)}
                      data-testid={`tenant-subscribe-${t.org_id}`}
                      title="Souscription auto-renouvelable (Stripe Subscription)"
                      className="h-8 px-3 rounded-lg bg-[#0F766E] text-white text-[12px] font-semibold hover:bg-[#115e59] mr-2 inline-flex items-center gap-1"
                    >
                      🔁 Souscrire
                    </button>
                  )}
                  <button
                    onClick={() => enterTenant(t)}
                    data-testid={`tenant-enter-${t.org_id}`}
                    title="Accéder à l'espace de cet OF (mode super admin)"
                    className="h-8 px-3 rounded-lg bg-[#4F46E5] text-white text-[12px] font-semibold hover:opacity-90 mr-2 inline-flex items-center gap-1"
                  >
                    <LogIn className="w-3.5 h-3.5" /> Accéder
                  </button>
                  <button
                    onClick={() => impersonate(t)}
                    data-testid={`tenant-impersonate-${t.org_id}`}
                    title="Se connecter en tant qu'admin de ce tenant (30 min)"
                    className="h-8 px-3 rounded-lg bg-white border border-[#4F46E5] text-[#4F46E5] text-[12px] font-semibold hover:bg-[#EEF2FF] mr-2 inline-flex items-center gap-1"
                  >
                    Impersonate
                  </button>
                  <button
                    onClick={() => onMetrics(t)}
                    data-testid={`tenant-metrics-${t.org_id}`}
                    className="h-8 px-3 rounded-lg bg-white border border-[#EFF0F6] text-[12px] font-semibold text-[#4A4B63] hover:bg-[#EEF2FF] hover:text-[#4F46E5] mr-2"
                  >
                    📊
                  </button>
                  {t.org_id !== 'org_default' && (
                    <button
                      onClick={() => deleteTenant(t)}
                      data-testid={`tenant-delete-${t.org_id}`}
                      className="h-8 w-8 rounded-lg text-[#B91C1C] hover:bg-[#FEF2F2] inline-flex items-center justify-center"
                      title="Supprimer ce tenant"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// Plans tab — modular tier editor (Vague 97.10)
// ---------------------------------------------------------------------------
function PlansTab({ plans, moduleCatalog, reload }) {
  const [editing, setEditing] = useState(null); // plan being edited

  const groupedCatalog = (moduleCatalog || []).reduce((acc, m) => {
    (acc[m.category] = acc[m.category] || []).push(m);
    return acc;
  }, {});

  const togglePlanModule = async (plan, moduleKey) => {
    const current = new Set(plan.modules || []);
    if (current.has(moduleKey)) current.delete(moduleKey);
    else current.add(moduleKey);
    try {
      await api.put(`/superadmin/plans/${plan.id}`, {
        ...plan,
        modules: Array.from(current),
      });
      // Invalidate FE features cache so the change is reflected immediately
      try { window.dispatchEvent(new CustomEvent('ifaap:features-changed')); } catch (e) { console.warn("[silent]", e); }
      await reload();
    } catch (e) {
      toast({ title: 'Erreur', description: e?.response?.data?.detail || 'Mise à jour impossible' });
    }
  };

  return (
    <>
      <div className="mb-4 flex items-center justify-between">
        <div>
          <h2 className="text-[18px] font-bold text-[#1A1B2E]" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Forfaits & modules</h2>
          <p className="text-[12.5px] text-[#6B6C85]">Cochez les modules inclus dans chaque forfait. L'effet de gamme est appliqué côté backend ET côté UI immédiatement.</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        {plans.map((p) => (
          <div key={p.code} data-testid={`plan-card-${p.code}`} className={`rounded-2xl border-2 p-5 ${p.code === 'pro' ? 'border-[#4F46E5] bg-[#EEF2FF]' : 'border-[#EFF0F6] bg-white'}`}>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-[18px] font-bold text-[#1A1B2E]" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{p.name}</h3>
              {p.code === 'pro' && <span className="text-[10.5px] font-bold px-2 py-0.5 rounded bg-[#4F46E5] text-white">RECOMMANDÉ</span>}
            </div>
            <div className="mb-4">
              {p.price_eur_month > 0 ? (
                <>
                  <span className="text-[28px] font-bold text-[#1A1B2E]">{p.price_eur_month}€</span>
                  <span className="text-[12.5px] text-[#6B6C85]">/mois</span>
                  {p.price_eur_year > 0 && <div className="text-[11px] text-[#4F46E5] mt-0.5">soit {p.price_eur_year}€/an</div>}
                </>
              ) : (
                <span className="text-[20px] font-bold text-[#1A1B2E]">Sur devis</span>
              )}
            </div>
            <div className="text-[11.5px] text-[#6B6C85] space-y-1.5 mb-3">
              <div>{p.max_users === -1 ? '∞' : p.max_users} utilisateurs</div>
              <div>{p.max_companies === -1 ? '∞' : p.max_companies} entreprises</div>
              <div>{p.max_sessions_per_month === -1 ? '∞' : p.max_sessions_per_month} sessions / mois</div>
            </div>
            <div className="border-t border-[#EFF0F6] pt-3 mt-3">
              <div className="text-[11px] font-semibold uppercase tracking-wide text-[#8E8FA5] mb-1.5">{(p.modules || []).length} modules inclus</div>
              <div className="text-[12px] text-[#4A4B63] line-clamp-3 mb-2">
                {(p.modules || []).map((k) => (moduleCatalog.find((m) => m.key === k) || {}).label || k).join(' · ') || 'Aucun module'}
              </div>
              <button
                data-testid={`plan-edit-modules-${p.code}`}
                onClick={() => setEditing(p)}
                className="w-full h-9 px-3 rounded-lg bg-white border border-[#4F46E5] text-[#4F46E5] text-[12.5px] font-semibold hover:bg-[#EEF2FF] transition-colors"
              >
                Configurer les modules →
              </button>
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
          <DialogContent className="max-w-[760px] max-h-[90vh] overflow-y-auto" data-testid="plan-modules-editor">
            <DialogHeader>
              <DialogTitle>Forfait — {editing.name}</DialogTitle>
              <DialogDescription>Modifiez le nom, les prix, les limites et les modules. Les changements sont appliqués immédiatement à tous les tenants sur ce plan.</DialogDescription>
            </DialogHeader>

            {/* Vague 99.0 — Inline editor for plan metadata */}
            <div className="rounded-xl bg-[#FAFAFE] border border-[#EFF0F6] p-4 mb-4 space-y-3">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <label className="block text-[11.5px] font-semibold text-[#1A1B2E]">Nom affiché
                  <input type="text" value={editing.name || ''} onChange={(e) => setEditing({ ...editing, name: e.target.value })} className="mt-1 w-full h-9 px-3 rounded-lg border border-[#EFF0F6] bg-white text-[12.5px]" data-testid="plan-edit-name" />
                </label>
                <label className="block text-[11.5px] font-semibold text-[#1A1B2E]">Code (interne)
                  <input type="text" value={editing.code || ''} disabled className="mt-1 w-full h-9 px-3 rounded-lg border border-[#EFF0F6] bg-[#EEF2FF] text-[12.5px] font-mono opacity-60" />
                </label>
                <label className="block text-[11.5px] font-semibold text-[#1A1B2E]">Prix mensuel (€)
                  <input type="number" value={editing.price_eur_month ?? 0} onChange={(e) => setEditing({ ...editing, price_eur_month: Number(e.target.value) })} className="mt-1 w-full h-9 px-3 rounded-lg border border-[#EFF0F6] bg-white text-[12.5px]" data-testid="plan-edit-price-month" />
                </label>
                <label className="block text-[11.5px] font-semibold text-[#1A1B2E]">Prix annuel (€)
                  <input type="number" value={editing.price_eur_year ?? 0} onChange={(e) => setEditing({ ...editing, price_eur_year: Number(e.target.value) })} className="mt-1 w-full h-9 px-3 rounded-lg border border-[#EFF0F6] bg-white text-[12.5px]" data-testid="plan-edit-price-year" />
                </label>
                <label className="block text-[11.5px] font-semibold text-[#1A1B2E]">Max utilisateurs (-1 = ∞)
                  <input type="number" value={editing.max_users ?? -1} onChange={(e) => setEditing({ ...editing, max_users: Number(e.target.value) })} className="mt-1 w-full h-9 px-3 rounded-lg border border-[#EFF0F6] bg-white text-[12.5px]" data-testid="plan-edit-max-users" />
                </label>
                <label className="block text-[11.5px] font-semibold text-[#1A1B2E]">Max entreprises (-1 = ∞)
                  <input type="number" value={editing.max_companies ?? -1} onChange={(e) => setEditing({ ...editing, max_companies: Number(e.target.value) })} className="mt-1 w-full h-9 px-3 rounded-lg border border-[#EFF0F6] bg-white text-[12.5px]" data-testid="plan-edit-max-companies" />
                </label>
                <label className="block text-[11.5px] font-semibold text-[#1A1B2E]">Max sessions / mois (-1 = ∞)
                  <input type="number" value={editing.max_sessions_per_month ?? -1} onChange={(e) => setEditing({ ...editing, max_sessions_per_month: Number(e.target.value) })} className="mt-1 w-full h-9 px-3 rounded-lg border border-[#EFF0F6] bg-white text-[12.5px]" data-testid="plan-edit-max-sessions" />
                </label>
                <label className="block text-[11.5px] font-semibold text-[#1A1B2E]">Période d'essai (jours)
                  <input type="number" value={editing.trial_days ?? 14} onChange={(e) => setEditing({ ...editing, trial_days: Number(e.target.value) })} className="mt-1 w-full h-9 px-3 rounded-lg border border-[#EFF0F6] bg-white text-[12.5px]" data-testid="plan-edit-trial-days" />
                </label>
              </div>
              <label className="block text-[11.5px] font-semibold text-[#1A1B2E]">Description (1 phrase commerciale)
                <input type="text" value={editing.description || ''} onChange={(e) => setEditing({ ...editing, description: e.target.value })} className="mt-1 w-full h-9 px-3 rounded-lg border border-[#EFF0F6] bg-white text-[12.5px]" data-testid="plan-edit-description" />
              </label>
              <label className="block text-[11.5px] font-semibold text-[#1A1B2E]">Avantages mis en avant (1 par ligne)
                <textarea
                  rows={3}
                  value={(editing.features || []).join('\n')}
                  onChange={(e) => setEditing({ ...editing, features: e.target.value.split('\n').map((s) => s.trim()).filter(Boolean) })}
                  className="mt-1 w-full px-3 py-2 rounded-lg border border-[#EFF0F6] bg-white text-[12.5px]"
                  data-testid="plan-edit-features"
                />
              </label>
              <div className="flex items-center gap-2">
                <button
                  data-testid="plan-edit-save"
                  onClick={async () => {
                    try {
                      await api.put(`/superadmin/plans/${editing.id}`, {
                        code: editing.code, name: editing.name,
                        price_eur_month: Number(editing.price_eur_month) || 0,
                        price_eur_year: Number(editing.price_eur_year) || 0,
                        max_users: Number(editing.max_users) ?? -1,
                        max_companies: Number(editing.max_companies) ?? -1,
                        max_sessions_per_month: Number(editing.max_sessions_per_month) ?? -1,
                        features: editing.features || [],
                        modules: editing.modules || [],
                        active: editing.active !== false,
                        description: editing.description || '',
                        trial_days: Number(editing.trial_days) ?? 14,
                      });
                      try { window.dispatchEvent(new CustomEvent('ifaap:features-changed')); } catch (e) { console.warn("[silent]", e); }
                      await reload();
                      toast({ title: 'Forfait mis à jour' });
                    } catch (e) {
                      toast({ title: 'Erreur', description: e?.response?.data?.detail || 'Sauvegarde impossible' });
                    }
                  }}
                  className="h-9 px-4 rounded-lg bg-[#0F766E] hover:bg-[#0E6D66] text-white text-[12.5px] font-semibold"
                >
                  💾 Enregistrer les modifications
                </button>
                {!['starter', 'pro', 'enterprise'].includes(editing.code) && (
                  <button
                    data-testid="plan-edit-delete"
                    onClick={async () => {
                      if (!window.confirm(`Supprimer définitivement le forfait "${editing.name}" ?`)) return;
                      try {
                        await api.delete(`/superadmin/plans/${editing.id}`);
                        await reload();
                        setEditing(null);
                        toast({ title: 'Forfait supprimé' });
                      } catch (e) {
                        toast({ title: 'Erreur', description: e?.response?.data?.detail || 'Suppression impossible' });
                      }
                    }}
                    className="h-9 px-3 rounded-lg bg-white border border-[#FECACA] text-[#B91C1C] text-[12.5px] font-semibold hover:bg-[#FEF2F2] flex items-center gap-1.5"
                  >
                    <Trash2 className="w-3.5 h-3.5" /> Supprimer
                  </button>
                )}
              </div>
            </div>

            <div className="text-[11px] font-bold uppercase tracking-wide text-[#4F46E5] mb-2">Modules inclus</div>
            <div className="max-h-[40vh] overflow-y-auto pr-2 space-y-4">
              {Object.entries(groupedCatalog).map(([cat, modules]) => (
                <div key={cat}>
                  <div className="text-[11px] font-bold uppercase tracking-wide text-[#4F46E5] mb-2">{cat}</div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {modules.map((m) => {
                      const enabled = (editing.modules || []).includes(m.key);
                      return (
                        <label
                          key={m.key}
                          data-testid={`plan-module-toggle-${editing.code}-${m.key}`}
                          className={`flex items-start gap-2.5 p-3 rounded-xl cursor-pointer border transition-colors ${enabled ? 'bg-[#EEF2FF] border-[#4F46E5]' : 'bg-white border-[#EFF0F6] hover:border-[#C5B8FF]'}`}
                        >
                          <input
                            type="checkbox"
                            checked={enabled}
                            onChange={async () => {
                              await togglePlanModule(editing, m.key);
                              const updated = await api.get('/superadmin/plans');
                              const next = updated.data.find((x) => x.code === editing.code);
                              if (next) setEditing(next);
                            }}
                            className="mt-0.5 w-4 h-4 rounded text-[#4F46E5] focus:ring-[#4F46E5]"
                          />
                          <div className="flex-1 min-w-0">
                            <div className="text-[13px] font-semibold text-[#1A1B2E]">{m.label}</div>
                            <div className="text-[11.5px] text-[#6B6C85] leading-snug">{m.description}</div>
                          </div>
                        </label>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
            <DialogFooter>
              <button
                onClick={() => setEditing(null)}
                className="h-10 px-4 rounded-xl brand-gradient text-white text-[13px] font-semibold"
              >
                Terminé
              </button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Create dialog
// ---------------------------------------------------------------------------
function CreateTenantDialog({ open, onOpenChange, plans, onCreated }) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [contactName, setContactName] = useState('');
  const [phone, setPhone] = useState('');
  const [siret, setSiret] = useState('');
  const [planCode, setPlanCode] = useState('starter');
  const [notes, setNotes] = useState('');
  const [sendInvite, setSendInvite] = useState(true);
  const [saving, setSaving] = useState(false);
  // Wave 1u8 — Show the invite outcome (link + delivery status) so the
  // super-admin can copy the link manually if email delivery fails.
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (open) {
      setName(''); setEmail(''); setContactName(''); setPhone(''); setSiret('');
      setPlanCode('starter'); setNotes(''); setSendInvite(true); setResult(null);
    }
  }, [open]);

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !email.trim()) { toast({ title: 'Raison sociale et email contact requis' }); return; }
    setSaving(true);
    try {
      const { data } = await api.post('/superadmin/tenants', {
        name, contact_email: email, contact_name: contactName,
        contact_phone: phone, siret, plan_code: planCode, notes,
        send_invite: sendInvite,
      });
      setResult(data?.invite || null);
      toast({
        title: 'Tenant créé',
        description: sendInvite
          ? (data?.invite?.delivered ? `Email d'activation envoyé à ${email}` : 'Tenant créé — copiez le lien d\'activation')
          : 'Tenant créé sans email',
      });
      onCreated();
    } catch (err) {
      toast({ title: 'Erreur', description: err?.response?.data?.detail || 'Création impossible' });
    } finally {
      setSaving(false);
    }
  };

  const copyLink = () => {
    if (!result?.link) return;
    try {
      navigator.clipboard.writeText(result.link);
      toast({ title: 'Lien copié' });
    } catch {
      toast({ title: 'Impossible de copier — sélectionnez le lien manuellement' });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[520px]" data-testid="tenant-create-dialog">
        <DialogHeader>
          <DialogTitle>Nouveau tenant</DialogTitle>
          <DialogDescription>L'organisme reçoit un email d'activation. Le contact définit son mot de passe puis accède à son espace en mode « Essai ».</DialogDescription>
        </DialogHeader>
        {result ? (
          <div className="space-y-3">
            <div className="rounded-xl border border-[#A7F3D0] bg-[#ECFDF5] p-4">
              <div className="flex items-center gap-2 text-[13px] font-bold text-[#065F46] mb-2">
                <span className="w-2 h-2 rounded-full bg-[#10B981]" />
                Tenant créé avec succès
              </div>
              <div className="text-[12px] text-[#064E3B] leading-relaxed">
                {result.delivered
                  ? <>L'email d'activation a été envoyé à <strong>{email}</strong>. Le lien expire dans 7 jours.</>
                  : <>L'email n'a pas pu être envoyé automatiquement. Transmettez le lien ci-dessous au contact :</>}
              </div>
            </div>
            <div>
              <label className="text-[11px] font-semibold text-[#6B6C85] uppercase tracking-wider block mb-1">Lien d'activation</label>
              <div className="flex items-center gap-2">
                <input readOnly value={result.link} data-testid="tenant-create-result-link" className="flex-1 h-9 px-3 rounded-lg bg-[#F6F7FB] border border-[#EFF0F6] text-[12px] font-mono" onClick={(e) => e.target.select()} />
                <button type="button" onClick={copyLink} data-testid="tenant-create-result-copy" className="btn-primary btn-sm">Copier</button>
              </div>
              <div className="text-[10.5px] text-[#8E8FA5] mt-1">
                Expire le {result.expires_at ? new Date(result.expires_at).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' }) : '—'}
              </div>
            </div>
            <DialogFooter>
              <button type="button" onClick={() => onOpenChange(false)} data-testid="tenant-create-close" className="btn-primary btn-md">Fermer</button>
            </DialogFooter>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <label className="text-[12.5px] font-semibold text-[#4A4B63] block mb-1.5">Raison sociale *</label>
                <input data-testid="tenant-create-name" required value={name} onChange={(e) => setName(e.target.value)} className="w-full h-10 px-3 rounded-xl bg-[#F6F7FB] border border-[#EFF0F6] text-[13.5px] focus:bg-white focus:border-[#4F46E5] outline-none" />
              </div>
              <div className="col-span-2">
                <label className="text-[12.5px] font-semibold text-[#4A4B63] block mb-1.5">Email contact administrateur *</label>
                <input data-testid="tenant-create-email" required type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="w-full h-10 px-3 rounded-xl bg-[#F6F7FB] border border-[#EFF0F6] text-[13.5px] focus:bg-white focus:border-[#4F46E5] outline-none" />
              </div>
              <div>
                <label className="text-[12.5px] font-semibold text-[#4A4B63] block mb-1.5">Nom du contact</label>
                <input data-testid="tenant-create-contact-name" value={contactName} onChange={(e) => setContactName(e.target.value)} placeholder="Prénom Nom" className="w-full h-10 px-3 rounded-xl bg-[#F6F7FB] border border-[#EFF0F6] text-[13.5px] focus:bg-white focus:border-[#4F46E5] outline-none" />
              </div>
              <div>
                <label className="text-[12.5px] font-semibold text-[#4A4B63] block mb-1.5">Téléphone</label>
                <input data-testid="tenant-create-phone" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+33 6 …" className="w-full h-10 px-3 rounded-xl bg-[#F6F7FB] border border-[#EFF0F6] text-[13.5px] focus:bg-white focus:border-[#4F46E5] outline-none" />
              </div>
              <div className="col-span-2">
                <label className="text-[12.5px] font-semibold text-[#4A4B63] block mb-1.5">SIRET</label>
                <input data-testid="tenant-create-siret" value={siret} onChange={(e) => setSiret(e.target.value)} placeholder="14 chiffres" className="w-full h-10 px-3 rounded-xl bg-[#F6F7FB] border border-[#EFF0F6] text-[13.5px] focus:bg-white focus:border-[#4F46E5] outline-none" />
              </div>
              <div className="col-span-2">
                <label className="text-[12.5px] font-semibold text-[#4A4B63] block mb-1.5">Forfait initial</label>
                <select data-testid="tenant-create-plan" value={planCode} onChange={(e) => setPlanCode(e.target.value)} className="w-full h-10 px-3 rounded-xl bg-[#F6F7FB] border border-[#EFF0F6] text-[13.5px]">
                  {plans.map((p) => <option key={p.code} value={p.code}>{p.name} ({p.price_eur_month > 0 ? `${p.price_eur_month}€/mois` : 'sur devis'})</option>)}
                </select>
              </div>
              <div className="col-span-2">
                <label className="text-[12.5px] font-semibold text-[#4A4B63] block mb-1.5">Notes internes</label>
                <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} className="w-full px-3 py-2 rounded-xl bg-[#F6F7FB] border border-[#EFF0F6] text-[13.5px] resize-none" />
              </div>
              <label className="col-span-2 inline-flex items-center gap-2 text-[12.5px] text-[#4A4B63] cursor-pointer">
                <input type="checkbox" checked={sendInvite} onChange={(e) => setSendInvite(e.target.checked)} data-testid="tenant-create-send-invite" />
                Envoyer l'email d'activation immédiatement
              </label>
            </div>
            <DialogFooter>
              <button type="button" onClick={() => onOpenChange(false)} className="btn-ghost btn-md">Annuler</button>
              <button type="submit" disabled={saving} data-testid="tenant-create-submit" className="btn-primary btn-md">
                {saving && <Loader2 className="w-4 h-4 animate-spin" />} Créer & envoyer
              </button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Metrics dialog
// ---------------------------------------------------------------------------
function TenantMetricsDialog({ tenant, onOpenChange }) {
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!tenant) { setMetrics(null); return; }
    setLoading(true);
    api.get(`/superadmin/tenants/${tenant.org_id}/metrics`)
      .then(({ data }) => setMetrics(data))
      .catch((e) => toast({ title: 'Erreur métriques', description: e?.response?.data?.detail || 'Indisponibles' }))
      .finally(() => setLoading(false));
  }, [tenant]);

  const items = metrics ? [
    { icon: Users, label: 'Utilisateurs', value: metrics.users },
    { icon: Building2, label: 'Entreprises', value: metrics.companies },
    { icon: GraduationCap, label: 'Sessions', value: metrics.sessions },
    { icon: Users, label: 'Apprenants', value: metrics.learners },
    { icon: FileText, label: 'Devis', value: metrics.quotes },
    { icon: FileText, label: 'Factures', value: metrics.invoices },
    { icon: Briefcase, label: 'Formateurs', value: metrics.trainers },
  ] : [];

  return (
    <Dialog open={!!tenant} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[480px]" data-testid="tenant-metrics-dialog">
        <DialogHeader>
          <DialogTitle>{tenant?.name}</DialogTitle>
          <DialogDescription>Métriques d'usage en temps réel · {tenant?.org_id}</DialogDescription>
        </DialogHeader>
        {loading ? (
          <div className="text-[13px] text-[#6B6C85] flex items-center gap-2 py-8 justify-center"><Loader2 className="w-4 h-4 animate-spin" /> Chargement…</div>
        ) : (
          <div className="grid grid-cols-2 gap-3">
            {items.map((it) => (
              <div key={it.label} className="rounded-xl bg-[#FAFAFE] border border-[#EFF0F6] p-3 flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-[#EEF2FF] flex items-center justify-center">
                  <it.icon className="w-4 h-4 text-[#4F46E5]" />
                </div>
                <div>
                  <div className="text-[20px] font-bold text-[#1A1B2E] tabular-nums">{it.value}</div>
                  <div className="text-[11px] text-[#6B6C85]">{it.label}</div>
                </div>
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}


// ---------------------------------------------------------------------------
// Metrics tab (Vague 97.11) — Cross-tenant SaaS dashboard
// ---------------------------------------------------------------------------
function MetricsTab() {
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [err, setErr] = React.useState(null);

  React.useEffect(() => {
    api.get('/superadmin/metrics')
      .then(({ data }) => setData(data))
      .catch((e) => setErr(e?.response?.data?.detail || 'Erreur de chargement'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="text-[14px] text-[#6B6C85] flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Chargement des metrics…</div>;
  if (err) return <div className="rounded-xl bg-[#FEF2F2] border border-[#FECACA] text-[#991B1B] px-4 py-3 text-[13px]">{err}</div>;
  if (!data) return null;

  const { totals, revenue, trial_conversion: tc, churn, top_plans, signups_timeline } = data;
  const fmtEur = (n) => `${(n || 0).toLocaleString('fr-FR', { maximumFractionDigits: 0 })} €`;

  // Hero KPIs (4 cards)
  const heroKpis = [
    revenue.theorique
      ? { label: 'MRR théorique', value: fmtEur(revenue.mrr_eur), sub: revenue.stripe_mode === 'live' ? 'Forfaits × espaces actifs — aucun encaissement enregistré' : `Forfaits × espaces actifs — Stripe en mode ${revenue.stripe_mode === 'test' ? 'test' : 'non configuré'}, rien n'est encaissé`, color: '#8E8FA5', icon: Euro }
      : { label: 'MRR', value: fmtEur(revenue.mrr_eur), sub: `ARR ${fmtEur(revenue.arr_eur)} · ${revenue.paiements_total} paiement(s) reçus au total`, color: '#4F46E5', icon: Euro },
    { label: 'Tenants actifs', value: totals.active, sub: `${totals.tenants} au total · ${totals.trial} essais`, color: '#14B8A6', icon: Building2 },
    { label: 'Conversion essai', value: `${tc.rate_pct}%`, sub: `${tc.converted_30d} / ${tc.trials_started_30d} (30j)`, color: '#F59E0B', icon: TrendingUp },
    { label: 'Churn (30j)', value: `${churn.rate_pct}%`, sub: `${churn.churned_30d} sur ${churn.active_30d_ago} actifs`, color: churn.rate_pct > 5 ? '#EF4444' : '#10B981', icon: TrendingDown },
  ];

  // Compute simple sparkline for signups (last 30 days, daily)
  const maxSignup = Math.max(...signups_timeline.map((s) => s.count), 1);

  return (
    <div className="space-y-6" data-testid="superadmin-metrics-tab">
      {/* Hero KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {heroKpis.map((k) => (
          <div key={k.label} data-testid={`metric-card-${k.label.toLowerCase().replace(/\s/g, '-')}`} className="soft-card p-5 relative overflow-hidden">
            <div className="absolute top-0 right-0 w-24 h-24 rounded-full opacity-10" style={{ background: k.color, transform: 'translate(30%, -30%)' }} />
            <div className="flex items-center justify-between relative">
              <div className="text-[11.5px] uppercase tracking-wide text-[#8E8FA5] font-semibold">{k.label}</div>
              <k.icon className="w-4 h-4" style={{ color: k.color }} />
            </div>
            <div className="text-[28px] font-bold mt-1.5 relative" style={{ color: k.color, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{k.value}</div>
            <div className="text-[11.5px] text-[#6B6C85] mt-0.5 relative">{k.sub}</div>
          </div>
        ))}
      </div>

      {/* Revenue band */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="soft-card p-5">
          <div className="flex items-center gap-2 mb-2">
            <Activity className="w-4 h-4 text-[#4F46E5]" />
            <h3 className="text-[14px] font-bold text-[#1A1B2E]">Revenu récent (30j)</h3>
          </div>
          <div className="text-[24px] font-bold text-[#1A1B2E]" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
            {fmtEur(revenue.recent_30d_eur)}
          </div>
          <div className="text-[11.5px] text-[#6B6C85] mt-0.5">{revenue.recent_30d_payments} paiement(s) reçu(s)</div>
        </div>

        <div className="soft-card p-5">
          <div className="flex items-center gap-2 mb-2">
            <Zap className="w-4 h-4 text-[#4F46E5]" />
            <h3 className="text-[14px] font-bold text-[#1A1B2E]">Statuts tenants</h3>
          </div>
          <div className="space-y-1.5 text-[12.5px]">
            <Row label="Actifs" value={totals.active} color="#10B981" />
            <Row label="En essai" value={totals.trial} color="#F59E0B" />
            <Row label="Suspendus" value={totals.suspended} color="#EF4444" />
            <Row label="Annulés" value={totals.cancelled} color="#6B6C85" />
          </div>
        </div>

        <div className="soft-card p-5">
          <div className="flex items-center gap-2 mb-2">
            <Calendar className="w-4 h-4 text-[#4F46E5]" />
            <h3 className="text-[14px] font-bold text-[#1A1B2E]">Inscriptions (30j)</h3>
          </div>
          {signups_timeline.length === 0 ? (
            <div className="text-[12.5px] text-[#6B6C85] mt-2">Aucune inscription sur la période.</div>
          ) : (
            <div className="flex items-end gap-0.5 h-16 mt-2" data-testid="metric-signups-spark">
              {signups_timeline.map((s) => (
                <div
                  key={s.date}
                  title={`${s.date} : ${s.count} inscription${s.count > 1 ? 's' : ''}`}
                  className="flex-1 bg-[#4F46E5] rounded-sm hover:bg-[#5B3FE0] transition-colors min-h-[2px]"
                  style={{ height: `${(s.count / maxSignup) * 100}%` }}
                />
              ))}
            </div>
          )}
          <div className="text-[11px] text-[#8E8FA5] mt-1">Total : {signups_timeline.reduce((sum, s) => sum + s.count, 0)} sur 30 jours</div>
        </div>
      </div>

      {/* Top plans */}
      <div className="soft-card p-5" data-testid="metric-top-plans">
        <h3 className="text-[14px] font-bold text-[#1A1B2E] mb-3" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Top forfaits par tenants actifs</h3>
        {top_plans.length === 0 ? (
          <div className="text-[12.5px] text-[#6B6C85]">Aucun tenant actif.</div>
        ) : (
          <div className="space-y-3">
            {top_plans.map((p) => {
              const total = top_plans.reduce((sum, x) => sum + x.tenant_count, 0) || 1;
              const pct = (p.tenant_count / total) * 100;
              return (
                <div key={p.code} data-testid={`metric-plan-${p.code}`}>
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center gap-2 text-[13px]">
                      <span className="font-bold text-[#1A1B2E]">{p.name}</span>
                      <span className="text-[11.5px] text-[#6B6C85]">· {fmtEur(p.price_eur_month)}/mois</span>
                    </div>
                    <div className="text-[12.5px] font-semibold text-[#4F46E5]">{p.tenant_count} tenant{p.tenant_count > 1 ? 's' : ''}</div>
                  </div>
                  <div className="h-2.5 bg-[#EEF2FF] rounded-full overflow-hidden">
                    <div className="h-full brand-gradient" style={{ width: `${pct}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div className="text-[11px] text-[#8E8FA5] text-right">
        Généré le {new Date(data.generated_at).toLocaleString('fr-FR')}
      </div>
    </div>
  );
}

function Row({ label, value, color }) {
  return (
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-2">
        <span className="w-2 h-2 rounded-full" style={{ background: color }} />
        <span className="text-[#4A4B63]">{label}</span>
      </div>
      <span className="font-bold text-[#1A1B2E]">{value}</span>
    </div>
  );
}


// ---------------------------------------------------------------------------
// Operations tab — Backup, Restore, Health (Vague 99.0)
// ---------------------------------------------------------------------------
function OpsTab() {
  const [preview, setPreview] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [confirmTxt, setConfirmTxt] = useState('');
  const [file, setFile] = useState(null);

  useEffect(() => {
    api.get('/superadmin/backup/preview').then((p) => setPreview(p.data)).catch(() => {});
  }, []);

  const doExport = async () => {
    setExporting(true);
    try {
      const res = await api.get('/superadmin/backup/export', { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `ofmanager-backup-${new Date().toISOString().slice(0, 10)}.json.gz`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast({ title: 'Backup téléchargé', description: 'Conservez ce fichier en lieu sûr.' });
    } catch (e) {
      toast({ title: 'Erreur backup', description: e?.response?.data?.detail || 'Échec' });
    }
    setExporting(false);
  };

  const doRestore = async () => {
    if (confirmTxt !== 'RESTORE' || !file) return;
    if (!window.confirm("⚠ Toutes les données actuelles seront REMPLACÉES par celles du backup. Continuer ?")) return;
    setRestoring(true);
    try {
      const fd = new FormData();
      fd.append('confirm', 'RESTORE');
      fd.append('file', file);
      const res = await api.post('/superadmin/backup/restore', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast({
        title: 'Restauration terminée',
        description: `${Object.keys(res.data.replaced || {}).length} collections restaurées${res.data.errors && Object.keys(res.data.errors).length ? ` · ${Object.keys(res.data.errors).length} erreurs` : ''}`,
      });
      setConfirmTxt(''); setFile(null);
    } catch (e) {
      toast({ title: 'Erreur restauration', description: e?.response?.data?.detail || 'Échec' });
    }
    setRestoring(false);
  };

  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-[18px] font-bold text-[#1A1B2E] mb-1" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Opérations & santé système</h2>
        <p className="text-[12.5px] text-[#6B6C85]">Ce que la plateforme sait d'elle-même à l'instant : la base, la sauvegarde de la nuit, les tâches, les boîtes, les erreurs.</p>
      </div>

      {/* La santé : des signaux réels. Les quatre compteurs de tenants qui
          étaient ici doublonnaient l'onglet Metrics. */}
      <Sante />

      {/* Backup */}
      <div className="rounded-2xl bg-white border border-[#EFF0F6] p-5">
        <div className="flex items-start gap-3 mb-3">
          <div className="w-10 h-10 rounded-xl bg-[#E6FBF7] text-[#0F766E] flex items-center justify-center shrink-0"><Download className="w-5 h-5" /></div>
          <div className="flex-1">
            <h3 className="text-[15px] font-bold text-[#1A1B2E]">Sauvegarde de la base</h3>
            <p className="text-[12px] text-[#6B6C85]">Génère un fichier <code className="text-[11px] bg-[#EEF2FF] px-1 py-0.5 rounded">.json.gz</code> de toutes les collections critiques. À télécharger et conserver hors-ligne.</p>
          </div>
          <button
            data-testid="ops-backup-btn"
            onClick={doExport}
            disabled={exporting}
            className="h-10 px-4 rounded-xl bg-[#0F766E] hover:bg-[#0E6D66] text-white text-[12.5px] font-semibold flex items-center gap-1.5 disabled:opacity-60"
          >
            <Download className="w-3.5 h-3.5" /> {exporting ? 'Export…' : 'Télécharger maintenant'}
          </button>
        </div>
        {preview && (
          <div className="text-[11.5px] text-[#6B6C85] mt-2">
            <strong className="text-[#1A1B2E]">{preview.total_documents.toLocaleString('fr-FR')}</strong> documents répartis dans {preview.collections} collections seront inclus.
          </div>
        )}
      </div>

      {/* Restore */}
      <div className="rounded-2xl bg-[#FEF2F2] border-2 border-[#FECACA] p-5" data-testid="ops-restore">
        <div className="flex items-start gap-3 mb-3">
          <div className="w-10 h-10 rounded-xl bg-[#FEE2E2] text-[#B91C1C] flex items-center justify-center shrink-0"><Upload className="w-5 h-5" /></div>
          <div className="flex-1">
            <h3 className="text-[15px] font-bold text-[#7F1D1D]">⚠ Restauration depuis un backup</h3>
            <p className="text-[12px] text-[#7F1D1D]">Action <strong>destructive</strong> — chaque collection est remplacée. Tapez <code className="bg-white px-1 rounded">RESTORE</code> pour confirmer.</p>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-2 items-center">
          <input
            type="file"
            accept=".gz,.json"
            onChange={(e) => setFile(e.target.files?.[0] || null)}
            data-testid="ops-restore-file"
            className="h-10 px-3 rounded-xl border border-[#FECACA] bg-white text-[12.5px] file:mr-2 file:px-2 file:py-1 file:rounded file:border-0 file:bg-[#FEE2E2] file:text-[#B91C1C]"
          />
          <input
            type="text"
            placeholder='Tapez "RESTORE" pour confirmer'
            value={confirmTxt}
            onChange={(e) => setConfirmTxt(e.target.value)}
            data-testid="ops-restore-confirm"
            className="h-10 px-3 rounded-xl border border-[#FECACA] bg-white text-[12.5px] font-mono"
          />
          <button
            data-testid="ops-restore-btn"
            onClick={doRestore}
            disabled={restoring || confirmTxt !== 'RESTORE' || !file}
            className="h-10 px-4 rounded-xl bg-[#B91C1C] hover:bg-[#991B1B] text-white text-[12.5px] font-semibold flex items-center justify-center gap-1.5 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <Upload className="w-3.5 h-3.5" /> {restoring ? 'Restauration…' : 'Restaurer la base'}
          </button>
        </div>
      </div>

      {/* OPCOs Bulk Seed — Wave 1n.3 */}
      <div className="rounded-2xl bg-white border border-[#EFF0F6] p-5" data-testid="ops-opcos-seed">
        <div className="flex items-center gap-2 mb-3">
          <div className="w-9 h-9 rounded-xl bg-[#EEF2FF] text-[#4F46E5] flex items-center justify-center">
            <Building2 className="w-4 h-4" />
          </div>
          <div>
            <h3 className="text-[14px] font-semibold text-[#1A1B2E]">Importer les 11 OPCOs officiels sur tous les tenants</h3>
            <p className="text-[11.5px] text-[#6B6C85]">Réparation : ajoute les 11 OPCO officiels (AFDAS, AKTO, ATLAS, etc.) à chaque organisation qui ne les possède pas. Idempotent (les OPCO existants sont préservés).</p>
          </div>
        </div>
        <button
          data-testid="ops-opcos-seed-btn"
          onClick={async () => {
            if (!window.confirm('Importer les 11 OPCOs officiels sur TOUS les tenants (et démos) ?\n\nIdempotent — aucun OPCO existant ne sera écrasé.')) return;
            try {
              const { data } = await api.post('/superadmin/opcos/seed-all');
              toast({
                title: `✅ Seed OPCOs terminé`,
                description: `${data.total_inserted} OPCO créés sur ${data.orgs_touched}/${data.orgs_processed} tenants.`,
              });
            } catch (err) {
              toast({ title: 'Erreur', description: err?.response?.data?.detail || 'Échec' });
            }
          }}
          className="h-10 px-4 rounded-xl bg-[#4F46E5] hover:bg-[#4338CA] text-white text-[12.5px] font-semibold flex items-center gap-1.5"
        >
          <Download className="w-3.5 h-3.5" /> Importer sur tous les tenants
        </button>
      </div>
    </div>
  );
}



// ---------------------------------------------------------------------------
// Audit log tab — cross-tenant audit log w/ filters + CSV export (Vague 99.0)
// ---------------------------------------------------------------------------
function AuditTab({ tenants }) {
  const [logs, setLogs] = useState([]);
  const [orgId, setOrgId] = useState('');
  const [action, setAction] = useState('');
  const [sinceDays, setSinceDays] = useState(30);
  const [loading, setLoading] = useState(false);

  const reload = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (orgId) params.set('org_id', orgId);
      if (action) params.set('action', action);
      params.set('since_days', String(sinceDays));
      params.set('limit', '500');
      const { data } = await api.get(`/superadmin/audit-logs?${params.toString()}`);
      setLogs(data || []);
    } catch (e) {
      toast({ title: 'Erreur', description: e?.response?.data?.detail || 'Audit log inaccessible' });
    }
    setLoading(false);
  };

  useEffect(() => { reload(); /* eslint-disable-next-line */ }, []);

  const downloadCsv = async () => {
    try {
      const params = new URLSearchParams();
      if (orgId) params.set('org_id', orgId);
      if (action) params.set('action', action);
      params.set('since_days', String(sinceDays));
      const res = await api.get(`/superadmin/audit-logs.csv?${params.toString()}`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url; a.download = `audit-logs-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      toast({ title: 'Erreur export', description: e?.response?.data?.detail || 'Échec' });
    }
  };

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-[18px] font-bold text-[#1A1B2E] mb-1" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Journal d'audit</h2>
        <p className="text-[12.5px] text-[#6B6C85]">Toutes les actions sensibles tous tenants confondus. Filtrable + export CSV.</p>
      </div>

      <div className="rounded-2xl bg-white border border-[#EFF0F6] p-4">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-2 mb-3">
          <select value={orgId} onChange={(e) => setOrgId(e.target.value)} data-testid="audit-filter-org" className="h-9 px-3 rounded-lg border border-[#EFF0F6] bg-white text-[12.5px]">
            <option value="">Tous les tenants</option>
            {(tenants || []).map((t) => <option key={t.org_id} value={t.org_id}>{t.name}</option>)}
          </select>
          <input type="text" placeholder="Action (ex: send_convention)" value={action} onChange={(e) => setAction(e.target.value)} data-testid="audit-filter-action" className="h-9 px-3 rounded-lg border border-[#EFF0F6] bg-white text-[12.5px]" />
          <select value={sinceDays} onChange={(e) => setSinceDays(Number(e.target.value))} data-testid="audit-filter-days" className="h-9 px-3 rounded-lg border border-[#EFF0F6] bg-white text-[12.5px]">
            <option value={1}>Dernières 24h</option>
            <option value={7}>7 jours</option>
            <option value={30}>30 jours</option>
            <option value={90}>90 jours</option>
            <option value={365}>1 an</option>
          </select>
          <button onClick={reload} className="h-9 px-3 rounded-lg bg-[#4F46E5] hover:bg-[#6B4DE8] text-white text-[12.5px] font-semibold flex items-center justify-center gap-1.5"><Filter className="w-3.5 h-3.5" /> Filtrer</button>
          <button onClick={downloadCsv} className="h-9 px-3 rounded-lg bg-white border border-[#EFF0F6] text-[#4A4B63] text-[12.5px] font-semibold flex items-center justify-center gap-1.5 hover:border-[#C7D2FE]"><Download className="w-3.5 h-3.5" /> Export CSV</button>
        </div>

        {loading ? (
          <div className="py-6 text-center text-[12.5px] text-[#6B6C85]"><Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Chargement…</div>
        ) : logs.length === 0 ? (
          <div className="py-8 text-center text-[12.5px] text-[#8E8FA5]">Aucun log pour ces filtres.</div>
        ) : (
          <div className="max-h-[500px] overflow-y-auto">
            <table className="w-full text-[11.5px]">
              <thead className="sticky top-0 bg-[#FAFAFE]">
                <tr className="text-left">
                  <th className="px-2 py-1.5 font-semibold text-[#6B6C85]">Date</th>
                  <th className="px-2 py-1.5 font-semibold text-[#6B6C85]">Tenant</th>
                  <th className="px-2 py-1.5 font-semibold text-[#6B6C85]">User</th>
                  <th className="px-2 py-1.5 font-semibold text-[#6B6C85]">Action</th>
                  <th className="px-2 py-1.5 font-semibold text-[#6B6C85]">Ressource</th>
                  <th className="px-2 py-1.5 font-semibold text-[#6B6C85]">IP</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#EFF0F6]" data-testid="audit-log-rows">
                {logs.map((l, i) => (
                  <tr key={l.id || i}>
                    <td className="px-2 py-1.5 font-mono text-[10.5px] text-[#6B6C85]">{l.at ? new Date(l.at).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' }) : '—'}</td>
                    <td className="px-2 py-1.5 text-[#4A4B63]">{l.org_id || '—'}</td>
                    <td className="px-2 py-1.5 text-[#4A4B63]">{l.user_email || l.user_name || '—'}</td>
                    <td className="px-2 py-1.5"><span className="font-mono text-[10.5px] bg-[#EEF2FF] text-[#4F46E5] px-1.5 py-0.5 rounded">{l.action}</span></td>
                    <td className="px-2 py-1.5 text-[#4A4B63]">{l.resource_type ? `${l.resource_type}/${l.resource_id || ''}` : '—'}</td>
                    <td className="px-2 py-1.5 font-mono text-[10.5px] text-[#8E8FA5]">{l.ip || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

